import { StarIcon, Orbit01Icon, SquareLock02Icon } from '@hugeicons-pro/core-solid-rounded';
import { BlurredExpoImage } from '@/components/ui/BlurredExpoImage';
import { LOCKED_CONTENT_BLUR } from '@/constants/lockedContentBlur';
import { Link } from 'expo-router';
import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform, TouchableOpacity, View } from 'react-native';

import { PresenceIndicator } from './PresenceIndicator';

import { Skeleton } from '@/components/gluestack/skeleton';
import { Text } from '@/components/gluestack/text';
import { Tooltip, TooltipContent, TooltipText } from '@/components/gluestack/tooltip';
import { VStack } from '@/components/gluestack/vstack';
import { getMemberPath } from '@/constants/routes';
import { Member } from '@/models/Member';
import { SocialTagIcons } from '~/components/domain/common/SocialTagIcons';
import { Box } from '~/components/gluestack/box';
import { Card } from '~/components/gluestack/card';
import { HStack } from '~/components/gluestack/hstack';
import { useMeasurementFormatters } from '~/components/providers/MeasurementsProvider';
import { CardLocationRow } from '~/components/ui/CardLocationRow';
import { HugeIconsWrapper } from '~/components/ui/HugeIconsWrapper';
import { SafeBlurView as BlurView } from '~/components/ui/SafeBlurView';
interface MemberCardProps {
  member?: Member;
  compact?: boolean;
  onLockedPress?: () => void;
}

const MemberCardComponent = ({ member, compact = false, onLockedPress }: MemberCardProps) => {
  const { t } = useTranslation();
  const { formatHeight, formatWeightSimple } = useMeasurementFormatters();

  if (!member) {
    return <MemberCardSkeleton compact={compact} />;
  }

  const details = [member.Age, formatHeight(member.HeightCm), formatWeightSimple(member.WeightKg)]
    .filter(Boolean)
    .join(' · ');

  const handlePress = () => {
    if (member.IsLocked) {
      onLockedPress?.();
    }
  };

  const cardContent = (
    <Card
      size="sm"
      className="p-0 web:transition-transform web:duration-200 web:ease-in-out web:hover:scale-105">
      <Box className="relative rounded-lg bg-background-200">
        <BlurredExpoImage
          source={{ uri: member.IconUrl }}
          contentFit="cover"
          className="aspect-square w-full rounded-lg"
          recyclingKey={member.IconUrl}
          blur={member.IsLocked ? LOCKED_CONTENT_BLUR : undefined}
          // Android: skip the crossfade — a fade per cell during grid scroll costs
          // more than it looks; iOS handles it without dropping frames.
          transition={Platform.OS === 'android' ? 0 : 200}
        />
        {member.IsLocked && (
          <View
            className="absolute inset-0 items-center justify-center rounded-lg bg-black/30"
            pointerEvents="none">
            <HugeIconsWrapper icon={SquareLock02Icon} size={48} className="text-white/70" />
          </View>
        )}
        {!member.IsLocked && (member.IsFollowing || member.IsFollower) && (
          <HStack className="absolute bottom-2 left-0 right-0 w-full items-center justify-center gap-1.5">
            {member.IsFollowing && (
              <Tooltip
                placement="top"
                trigger={(triggerProps) => (
                  <Box {...triggerProps} className="size-8 overflow-hidden rounded-full">
                    <BlurView
                      intensity={80}
                      tint="dark"
                      className="h-full w-full items-center justify-center"
                      blurMethod="none">
                      <HugeIconsWrapper icon={StarIcon} size={18} className="color-yellow-500" />
                    </BlurView>
                  </Box>
                )}>
                <TooltipContent className="bg-background-700">
                  <TooltipText className="text-typography-50">{t('common.followed')}</TooltipText>
                </TooltipContent>
              </Tooltip>
            )}
            {member.IsFollower && (
              <Tooltip
                placement="top"
                trigger={(triggerProps) => (
                  <Box {...triggerProps} className="size-8 overflow-hidden rounded-full">
                    <BlurView
                      intensity={80}
                      tint="dark"
                      className="h-full w-full items-center justify-center"
                      blurMethod="none">
                      <HugeIconsWrapper icon={Orbit01Icon} size={18} className="color-amber-500" />
                    </BlurView>
                  </Box>
                )}>
                <TooltipContent className="bg-background-700">
                  <TooltipText className="text-typography-50">
                    {t('profile.follows_you')}
                  </TooltipText>
                </TooltipContent>
              </Tooltip>
            )}
          </HStack>
        )}
      </Box>
      <VStack className="items-center gap-1 px-2 pb-3 pt-2">
        {member.UserName && (
          <HStack className="w-full items-center justify-center gap-1.5">
            <HStack className="min-w-0 shrink items-center">
              {Platform.OS !== 'web' && (
                // Zero-width glyph pins the row to the unshrunk line height, so cards
                // stay the same height when adjustsFontSizeToFit scales a long name down.
                <Text bold size={compact ? 'sm' : 'md'} className="w-0">
                  {' '}
                </Text>
              )}
              <Text
                bold
                isTruncated
                size={compact ? 'sm' : 'md'}
                // The new-arch fitter has no working floor (minimumFontScale never reaches
                // native, and the minimumFontSize fallback is filtered by the Text view
                // config), so narrow compact cards skip it: tiny text + Android px drift.
                adjustsFontSizeToFit={!compact}
                minimumFontScale={0.85}
                className="min-w-0 shrink">
                {member.UserName}
              </Text>
            </HStack>
            <PresenceIndicator member={member} showLastSeen={!compact && !member.IsLocked} />
          </HStack>
        )}
        {!compact &&
          (member.SocialTags?.length > 0 ? (
            <SocialTagIcons
              tags={member.SocialTags}
              size={20}
              className="max-w-full overflow-hidden"
            />
          ) : (
            <View style={{ height: 20 }} />
          ))}
        {!compact && details && (
          <Text size="sm" isTruncated className="max-w-full">
            {details}
          </Text>
        )}
        <CardLocationRow
          placeName={compact ? undefined : member.PlaceNameShort}
          placeNameFull={compact ? undefined : member.PlaceNameCleaned}
          countryCode={member.CountryCode}
          distanceKm={member.DistanceKm}
        />
      </VStack>
    </Card>
  );

  return member.IsLocked ? (
    <TouchableOpacity activeOpacity={0.8} onPress={handlePress}>
      {cardContent}
    </TouchableOpacity>
  ) : (
    <Link href={getMemberPath(member)} asChild>
      <TouchableOpacity activeOpacity={0.8}>{cardContent}</TouchableOpacity>
    </Link>
  );
};

export const MemberCard = memo(MemberCardComponent);

function MemberCardSkeleton({ compact = false }: Pick<MemberCardProps, 'compact'>) {
  return (
    <Card size="sm" className="p-0">
      <Box className="relative aspect-square w-full">
        <Skeleton variant="rounded" className="absolute inset-0 h-full w-full" />
      </Box>
      <VStack className="items-center gap-2 p-4">
        <Skeleton variant="rounded" className={compact ? 'h-4 w-20' : 'h-5 w-24'} />
        {!compact && (
          <>
            <Skeleton variant="rounded" className="h-3 w-32" />
            <Skeleton variant="rounded" className="h-3 w-28" />
          </>
        )}
        <Skeleton variant="rounded" className="h-3 w-20" />
      </VStack>
    </Card>
  );
}
